import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MeetingPrepCard } from "@/components/intelligence/MeetingPrepCard";
import { ActionPlanPanel } from "@/components/intelligence/ActionPlanPanel";
import { useLang } from "@/i18n";

/** Structured Athlynx Intelligence response — operational report, not a chat bubble. */
export const IntelligenceResponse = ({ r, onGeneratePlan, onFollowUp, asking }) => {
  const { tf } = useLang();
  return (
  <div className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4" data-testid="intel-answer">
    {r.degraded && (
      <p className="text-[11px] text-amber-600 dark:text-amber-500">
        {tf("Une partie de l'analyse n'a pas pu être générée. Vos données Athlynx restent disponibles.")}
      </p>
    )}
    <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="intel-answer-summary">{r.summary}</p>
    {r.meeting_prep && <MeetingPrepCard prep={r.meeting_prep} onGeneratePlan={onGeneratePlan} asking={asking} />}
    {(r.entities || []).map((e, i) => (
      <div key={i} className="rounded-xl border border-border bg-secondary/30 p-3" data-testid={`intel-entity-${i}`}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold">{e.name}</p>
          {e.link && (
            <Link to={e.link} className="text-xs text-athlynx hover:underline inline-flex items-center gap-1">
              {tf("Ouvrir")} <ArrowUpRight className="h-3 w-3" />
            </Link>
          )}
        </div>
        <ul className="mt-1.5 space-y-1">
          {(e.signals || []).map((s, j) => (
            <li key={j} className="text-xs text-muted-foreground flex items-start gap-1.5">
              <span className="h-1 w-1 rounded-full bg-athlynx mt-1.5 shrink-0" /> {s}
            </li>
          ))}
        </ul>
        {e.recommended_action && (
          <p className="text-xs mt-2 font-medium text-foreground/90">{tf("Action recommandée :")} {e.recommended_action}</p>
        )}
      </div>
    ))}
    {(r.deadlines || []).length > 0 && (
      <div className="space-y-1">
        {r.deadlines.map((d, i) => (
          <Link key={i} to={d.link} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid={`intel-answer-deadline-${i}`}>
            <span className="font-semibold text-athlynx tabular-nums">{d.days} j</span>
            <span className="truncate">{d.title}</span>
          </Link>
        ))}
      </div>
    )}
    {(r.follow_up_questions || []).length > 0 && (
      <div className="flex flex-wrap gap-1.5 pt-1">
        {r.follow_up_questions.map((q, i) => (
          <button
            key={i}
            onClick={() => onFollowUp?.(q)}
            disabled={asking}
            data-testid={`intel-followup-${i}`}
            className="text-[11px] text-muted-foreground border border-border rounded-full px-2.5 py-1 hover:text-foreground hover:border-foreground/30 transition-colors disabled:opacity-50"
          >
            {q}
          </button>
        ))}
      </div>
    )}
    {Array.isArray(r.action_plan) && r.action_plan.length > 0 && <ActionPlanPanel plan={r.action_plan} />}
  </div>
);
};
